interface Infrastructure {
  parentItemId: string;
  childItemId: string;
  infrastructureType: string;
  circuitId?: string;
}

interface ItemWithConfig {
  infrastructureRelationshipConfigs?: {
    infrastructureType: string;
    load: number;
    unit?: string;
  }[];
}

export interface CircuitTotalLoad {
  load: number | null;
  unit?: string;
  hasMixedUnits: boolean;
}

export function calculateCircuitTotalLoads(params: {
  infrastructures: Infrastructure[];
  currentItemId: string;
  getItem: (id: string) => ItemWithConfig | undefined;
}): Record<string, CircuitTotalLoad | undefined> {
  const { infrastructures, currentItemId, getItem } = params;
  const result: Record<string, CircuitTotalLoad | undefined> = {};
  const unitsByCircuit: Record<string, Set<string | undefined> | undefined> = {};

  for (const infra of infrastructures) {
    if (infra.infrastructureType !== 'electricity') continue;
    if (infra.parentItemId !== currentItemId) continue;
    if (!infra.circuitId) continue;

    const childItem = getItem(infra.childItemId);
    if (!childItem) continue;

    const config = childItem.infrastructureRelationshipConfigs?.find(
      (c) => c.infrastructureType === 'electricity',
    );
    const load = config?.load ?? 0;
    const existingTotal = result[infra.circuitId];

    result[infra.circuitId] = {
      load: (existingTotal?.load ?? 0) + load,
      unit: existingTotal?.unit ?? config?.unit,
      hasMixedUnits: false,
    };

    const units = unitsByCircuit[infra.circuitId] ?? new Set<string | undefined>();
    units.add(config?.unit);
    unitsByCircuit[infra.circuitId] = units;
  }

  for (const [circuitId, units] of Object.entries(unitsByCircuit)) {
    const total = result[circuitId];
    if (!units) continue;
    if (units.size > 1 && total) {
      total.hasMixedUnits = true;
      total.load = null;
      total.unit = undefined;
    }
  }

  return result;
}
